/**
 * Utility function to format field values for display in Treasury Platform Management components.
 * Handles various data types including null/undefined values, objects, booleans, dates, and strings.
 */

/**
 * Formats field values for display, showing "null" for null/undefined values
 * @param value - The value to format (can be any type)
 * @returns Formatted string representation of the value
 */
export const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "null";
  }

  if (typeof value === "object") {
    // Handle currency objects with amount and currency properties
    if (
      value !== null &&
      typeof value === "object" &&
      "amount" in value &&
      "currency" in value
    ) {
      const currencyObj = value;
      return `${String(currencyObj.currency)} ${String(currencyObj.amount)}`;
    }
    return JSON.stringify(value);
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  // Handle date strings - if it looks like an ISO date, format it nicely
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }

  // For all other types, convert to string safely
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  // For any other type, use JSON.stringify as fallback
  return JSON.stringify(value);
};
